
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Poll, PollStatus } from '@/types/poll';
import { recalculatePollStatistics } from '@/utils/polls/pollAdminUtils';
import { updateLinkedCardFromMultiplePolls } from '@/utils/polls/pollCardLinking';

export const usePollReopening = (
  polls: Poll[], 
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>
) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleReopenPoll = async (id: string) => {
    try {
      setIsProcessing(true);
      const { error } = await supabase
        .rpc('reopen_poll', { poll_id: id });
      
      if (error) {
        toast.error('Failed to reopen poll');
        return;
      }
      
      // Update local poll state
      const updatedPolls = polls.map(poll => 
        poll.id === id ? { ...poll, status: 'live' as PollStatus } : poll
      );
      setPolls(updatedPolls);

      // Recalculate statistics when reopening a poll
      try {
        await recalculatePollStatistics(id);
        
        // Find all polls linked to the same card(s)
        const linkedPolls = updatedPolls.filter(
          poll => poll.linked_card_id === updatedPolls.find(p => p.id === id)?.linked_card_id
        );
        
        // Update linked card statistics for all linked polls
        if (linkedPolls.length > 0 && linkedPolls[0].linked_card_id) {
          await updateLinkedCardFromMultiplePolls(linkedPolls);
        }
        
        console.log('Poll statistics recalculated after reopening poll', id);
      } catch (recalcError) {
        console.error('Error recalculating poll statistics after reopening:', recalcError);
      }
      
      toast.success('Poll reopened');
    } catch (error) {
      console.error('Error reopening poll:', error);
      toast.error('An error occurred while reopening the poll');
    } finally {
      setIsProcessing(false);
    }
  };

  return { handleReopenPoll, isProcessing };
};
